import {Injectable} from '@angular/core';
import {flatMap, shareReplay} from 'rxjs/operators';
import {forkJoin} from 'rxjs/internal/observable/forkJoin';
import {first} from 'rxjs/internal/operators/first';
import {tap} from 'rxjs/internal/operators/tap';
import {ValueOneElementHelper} from './value-one-element-helper';
import {ElementWriterService} from './element-writer.service';
import {NPElementRepository} from './np-element-repository';
import {NPElement} from '../model';

interface MediaToCreate {
    File: File;
    ParentDirectoryExtID: string;
}

@Injectable({
    providedIn: 'root'
})
export class MediaCreationService {
    private _modifListByElement: Map<string, Map<string, MediaToCreate[]>> = new Map<string, Map<string, MediaToCreate[]>>();
    private readonly _valueHelper: ValueOneElementHelper;

    constructor(private _elementWriter: ElementWriterService,
                private _elementReader: NPElementRepository) {
        this._valueHelper = new ValueOneElementHelper();
    }

    public setMedias(element: NPElement, dicoCaracExtID: string, medias: MediaToCreate[]) {
        if (!this._modifListByElement.has(element.ExtID)) {
            this._modifListByElement.set(element.ExtID, new Map<string, MediaToCreate[]>());
        }
        this._modifListByElement.get(element.ExtID).set(dicoCaracExtID, medias);
        if (medias.length === 0) {
            this.deleteMediaByDicoCaracExtId(element, dicoCaracExtID);
        }
    }

    public cancelAllModifications(elementExtId: string) {
        if (elementExtId && elementExtId !== '' && this._modifListByElement.has(elementExtId)) {
            this._modifListByElement.delete(elementExtId);
        }
    }

    public deleteMediaByDicoCaracExtId(element: NPElement, dicoCaracExtID: string) {
        if (this._modifListByElement.has(element.ExtID) && this._modifListByElement.get(element.ExtID).has(dicoCaracExtID)) {
            this._modifListByElement.get(element.ExtID).delete(dicoCaracExtID);
        }
        if (this._modifListByElement.get(element.ExtID).size <= 0) {
            this.flushByElementExtId(element.ExtID);
        }
    }

    public deleteMedia(element: NPElement, dicoCaracExtID: string) {
        this._modifListByElement.get(element.ExtID).delete(dicoCaracExtID);
    }

    /**
     * Retourne tous les medias de l'élement passé en paramètre
     */
    public getAllMediasByElementExtId(elementExtId: string) {
        return this._modifListByElement.get(elementExtId);
    }

    /**
     * Retourne les fichiers relatifs à un dicocarac
     */
    public getMediaByDicoCaracExtId(elementExtId: string, dicoCaracExtid: string) {
        if (this.getAllMediasByElementExtId(elementExtId) === undefined || this.getAllMediasByElementExtId(elementExtId).get(dicoCaracExtid) === undefined) {
            return null;
        } else {
            return this.getAllMediasByElementExtId(elementExtId).get(dicoCaracExtid);
        }
    }

    public flush() {
        this._modifListByElement = new Map<string, Map<string, MediaToCreate[]>>();
    }

    public flushByElementExtId(elmentExtID: string) {
        this._modifListByElement.delete(elmentExtID);
    }

    public hasModifications() {
        return this._modifListByElement && this._modifListByElement.size > 0;
    }

    public publishMedias() {
        const arrayParentExtIDs = [];
        const elementExtIDs = [];
        this._modifListByElement.forEach((caracteristiques, elementExtID) => {
            elementExtIDs.push(elementExtID);
            caracteristiques.forEach((mediasToCreate) => {
                mediasToCreate.forEach(media => arrayParentExtIDs.push(media.ParentDirectoryExtID));
            });
        });
        const elements$ = this._elementReader.getElements([...arrayParentExtIDs, ...elementExtIDs], []).pipe(
            first(),
            shareReplay(1)
        );
        return elements$.pipe(
            flatMap((elements) => {
                const arrayObservablesResult = [];
                this._modifListByElement.forEach((caracteristiques, elementExtID) => {
                    caracteristiques.forEach((mediasToCreate, caracExtID) => {
                        mediasToCreate.forEach(media => {
                            arrayObservablesResult.push(this._elementWriter.createMedia(media.File, elements.get(media.ParentDirectoryExtID).ID).pipe(
                                first(),
                                tap(newMedia => {
                                    this._elementWriter.setValueLink(elements.get(elementExtID), caracExtID, [newMedia]);
                                })
                            ));
                        });
                    });
                });
                this._modifListByElement = new Map<string, Map<string, MediaToCreate[]>>();
                return forkJoin(arrayObservablesResult);
            }),
            flatMap(result => {
                return this._elementWriter.publishToNP();
            })
        );
    }

    public publishMediasByElementExtId(npElementExtID: string) {
        const arrayParentExtIDs = [];
        const elementExtIDs = [];
        this._modifListByElement.forEach((caracteristiques, elementExtID) => {
            // On ne traite que les médias de l'élement en cours
            if (elementExtID === npElementExtID) {
                // console.log('elementExtID === npElementExtID : ', elementExtID);
                elementExtIDs.push(elementExtID);
                caracteristiques.forEach((mediasToCreate) => {
                    mediasToCreate.forEach(media => arrayParentExtIDs.push(media.ParentDirectoryExtID));
                });
            }
        });
        const elements$ = this._elementReader.getElements([...arrayParentExtIDs, ...elementExtIDs], []).pipe(
            first(),
            shareReplay(1)
        );
        return elements$.pipe(
            flatMap((elements) => {
                const arrayObservablesResult = [];
                this._modifListByElement.forEach((caracteristiques, elementExtID) => {
                    if (elementExtID === npElementExtID) {
                        caracteristiques.forEach((mediasToCreate, caracExtID) => {
                            mediasToCreate.forEach(media => {
                                arrayObservablesResult.push(this._elementWriter.createMedia(media.File, elements.get(media.ParentDirectoryExtID).ID).pipe(
                                    first(),
                                    tap(newMedia => {
                                        this._elementWriter.setValueLink(elements.get(elementExtID), caracExtID, [newMedia]);
                                    })
                                ));
                            });
                        });
                    }
                });
                // this._modifListByElement = new Map<string, Map<string, MediaToCreate[]>>();
                return forkJoin(arrayObservablesResult);
            }),
            flatMap(result => {
                return this._elementWriter.publishToNP();
            })
        );
    }
}
